/*
 * Combine Home — Configurador 3D da mesa de centro modular (v1)
 * ES module. Requer um importmap com "three" e "three/addons/" (ver theme.liquid).
 * Lê a configuração do <script type="application/json" data-cfg-config> dentro de [data-cfg-root].
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const CODE_RE = /^([TLFX][0-3][nce]\.){3}[TLFX][0-3][nce]$/;

const boot = () => {
  document.querySelectorAll('[data-cfg-trigger-wrap]:not([data-cfg-placed])').forEach(placeTrigger);
  document.querySelectorAll('[data-cfg-drawer]:not([data-cfg-bound])').forEach(bindDrawer);
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

// Coloca o botão "Configurar" logo a seguir ao botão de compra do tema (Shella)
function placeTrigger(wrap) {
  wrap.setAttribute('data-cfg-placed', '');
  const anchor = document.querySelector('.product-page-info__button-add-to-cart')
    || document.querySelector('.product-page-info form[action*="/cart/add"]');
  if (anchor) anchor.insertAdjacentElement('afterend', wrap);
  wrap.hidden = false;
}

// Aba lateral (abre da direita para a esquerda). O 3D só arranca na primeira abertura.
function bindDrawer(drawer) {
  drawer.setAttribute('data-cfg-bound', '');
  const panel = drawer.querySelector('.cfg-drawer__panel');
  let api = null, lastFocus = null;
  const open = () => {
    lastFocus = document.activeElement;
    drawer.hidden = false;
    document.documentElement.classList.add('cfg-lock');
    requestAnimationFrame(() => requestAnimationFrame(() => drawer.classList.add('is-open')));
    if (!api) api = initConfigurator(drawer.querySelector('[data-cfg-root]'), { close });
    api.setActive(true);
    const focusEl = drawer.querySelector('.cfg-drawer__close') || drawer.querySelector('[data-cfg-close]');
    if (focusEl) focusEl.focus({ preventScroll: true });
    // Ensure the drawer head is hidden on mobile/tablet (defensive: some browsers ignore CSS !important on inline flex containers)
    if (window.matchMedia('(max-width: 1024px)').matches) {
      const head = drawer.querySelector('.cfg-drawer__head');
      if (head) head.style.display = 'none';
      const body = drawer.querySelector('.cfg-drawer__body');
      if (body) body.style.paddingTop = '0';
    }
  };
  const close = () => {
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('cfg-lock');
    if (api) api.setActive(false);
    try { history.replaceState(history.state, '', location.pathname + location.search); } catch (e) {}
    const done = () => { if (!drawer.classList.contains('is-open')) drawer.hidden = true; };
    panel.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 500);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  };
  document.addEventListener('click', e => { if (e.target.closest('[data-cfg-open]')) { e.preventDefault(); open(); } });
  drawer.querySelectorAll('[data-cfg-close]').forEach(b => b.addEventListener('click', close));
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open') && !drawer.querySelector('dialog[open]')) close();
  });
  const h = decodeURIComponent(location.hash.slice(1));
  if (h === 'configurar' || CODE_RE.test(h)) open();
}

function initConfigurator(root, hooks = {}) {
  root.setAttribute('data-cfg-ready', '');
  const CFG = JSON.parse(root.querySelector('script[data-cfg-config]').textContent);
  const q = s => root.querySelector(`[data-cfg-${s}]`);

  /* ---------- Catálogo ---------- */
  const LABELS = CFG.labels || {};
  const POS = LABELS.positions || ['Back left', 'Back right', 'Front left', 'Front right'];
  const MOD_WORD = LABELS.modules || 'modules';
  const EMPTY_LABEL = LABELS.empty || 'No module';
  const TYPES = {}, MATS = {};
  CFG.types.forEach(t => TYPES[t.key] = t);
  const EMPTY = 'X';
  TYPES[EMPTY] = { key: EMPTY, label: EMPTY_LABEL };
  const filled = () => state.cells.filter(c => c.t !== EMPTY);
  CFG.materials.forEach(m => MATS[m.key] = m);
  const CELLS = [
    { label: POS[0], x: -1, z: -1 },
    { label: POS[1], x: 1, z: -1 },
    { label: POS[2], x: -1, z: 1 },
    { label: POS[3], x: 1, z: 1 },
  ];
  const DEFAULT_CODE = CFG.defaultCode || 'T0n.L1n.L0n.F0n';
  // Medidas de cada módulo em metros (mesa 2×2 = 100 × 100 × 30 cm). Os modelos são ajustados a estas medidas.
  const MOD = { w: (CFG.module && CFG.module.w) || 0.5, h: (CFG.module && CFG.module.h) || 0.3 };
  const GAP = 0.004, HALF = MOD.w / 2 + GAP / 2, TILE = 0.8;

  /* ---------- Estado ---------- */
  const decode = code => {
    const parts = String(code || '').split('.');
    if (parts.length !== 4) return null;
    const out = parts.map(p => ({ t: p[0], r: +p[1] % 4, m: p[2] }));
    return out.every(c => TYPES[c.t] && MATS[c.m] && Number.isInteger(c.r)) ? out : null;
  };
  const state = { cells: decode(DEFAULT_CODE), sel: 2 };
  const encode = () => state.cells.map(c => c.t + c.r + c.m).join('.');
  { const h = decodeURIComponent(location.hash.slice(1)); const d = decode(h); if (d) state.cells = d; }

  /* ---------- Variantes e preço ---------- */
  const norm = s => String(s).trim().toLowerCase();
  const findVariant = c => {
    // Match by label (locale-aware). Shopify renders variant.options translated for the current locale,
    // and the widget's type/material labels come from the same locale via the Liquid config — so they match.
    const T = TYPES[c.t], M = MATS[c.m];
    const a = norm(T.label);
    const b = norm(M.label);
    return (CFG.variants || []).find(v => { const o = v.options.map(norm); return o.includes(a) && o.includes(b); });
  };
  const money = cents => moneyRaw(cents).replace(/<[^>]*>/g, '');
  const moneyRaw = cents => {
    const fmt = CFG.moneyFormat || '€{{amount_no_decimals}}';
    try { if (window.Shopify && typeof window.Shopify.formatMoney === 'function') return window.Shopify.formatMoney(cents, fmt); } catch (e) {}
    const n = (cents / 100);
    const group = (v, d, th, dec) => { const [i, f] = v.toFixed(d).split('.'); return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (f ? dec + f : ''); };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: group(n, 2, ',', '.'),
      amount_no_decimals: group(n, 0, ',', '.'),
      amount_with_comma_separator: group(n, 2, '.', ','),
      amount_no_decimals_with_comma_separator: group(n, 0, '.', ','),
    })[k] ?? group(n, 2, ',', '.'));
  };
  const priceOf = c => { if (c.t === EMPTY) return 0; const v = findVariant(c); return v ? v.price : (CFG.fallbackPrice || 0); };

  /* ---------- Ícones ---------- */
  const planIcon = c => {
    if (c.t === EMPTY) return '<svg viewBox="0 0 40 40" aria-hidden="true"><rect x="3.5" y="3.5" width="33" height="33" fill="none" stroke="#b5b5b5" stroke-dasharray="3 3"/><path d="M20 14v12M14 20h12" stroke="#9a9a9a" stroke-width="1.4"/></svg>';
    const col = MATS[c.m].color, dark = 'rgba(0,0,0,.38)', light = 'rgba(255,255,255,.3)';
    let inner = '';
    if (c.t === 'T') inner = `<rect x="7" y="7" width="26" height="26" fill="${dark}"/>`;
    if (c.t === 'L') inner = `<rect x="7" y="4" width="26" height="32" fill="${dark}" opacity=".45"/><rect x="3" y="3" width="4" height="34" fill="${light}"/><rect x="33" y="3" width="4" height="34" fill="${light}"/>`;
    return `<svg viewBox="0 0 40 40" style="transform:rotate(${c.r * 90}deg)" aria-hidden="true"><rect x="3" y="3" width="34" height="34" fill="${col}"/>${inner}</svg>`;
  };
  const TYPE_SVG = {
    T: '<svg viewBox="0 0 44 36" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M6 8v22h32V8"/><path d="M10 8v18h24V8"/><path d="M9 33h26"/></svg>',
    L: '<svg viewBox="0 0 44 36" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M6 6v24h32V6"/><path d="M6 9h32"/><rect x="10" y="12" width="24" height="15"/><path d="M9 33h26"/></svg>',
    F: '<svg viewBox="0 0 44 36" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><rect x="6" y="6" width="32" height="24" fill="currentColor" fill-opacity=".12"/><path d="M9 33h26" fill="none"/></svg>',
    X: '<svg viewBox="0 0 44 36" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><rect x="6" y="6" width="32" height="24" stroke-dasharray="3 3" opacity=".6"/><path d="M16 12l12 12M28 12L16 24" opacity=".6"/></svg>',
  };

  /* ---------- UI ---------- */
  q('types').innerHTML = [...CFG.types, TYPES[EMPTY]].map(t =>
    `<button class="cfg-opt" type="button" data-type="${t.key}" aria-pressed="false">${TYPE_SVG[t.key] || ''}<span>${t.label}</span></button>`).join('');
  q('mats').innerHTML = CFG.materials.map(m =>
    `<button class="cfg-sw" type="button" data-mat="${m.key}" aria-pressed="false" title="${m.label}" aria-label="${m.label}"><img src="${m.thumb}" alt="" loading="lazy" width="52" height="52"></button>`).join('');

  q('types').addEventListener('click', e => { const b = e.target.closest('[data-type]'); if (b) { state.cells[state.sel].t = b.dataset.type; update(); } });
  q('mats').addEventListener('click', e => { const b = e.target.closest('[data-mat]'); if (b) { state.cells[state.sel].m = b.dataset.mat; update(); } });
  q('rotate').addEventListener('click', () => { const c = state.cells[state.sel]; c.r = (c.r + 1) % 4; update(); });
  q('applyall').addEventListener('click', () => { const m = state.cells[state.sel].m; state.cells.forEach(c => c.m = m); update(); toast(`${MATS[m].label} → ${MOD_WORD}`); });
  q('plan').addEventListener('click', e => { const b = e.target.closest('[data-cell]'); if (b) { state.sel = +b.dataset.cell; update(); } });
  q('reset').addEventListener('click', resetView);
  q('ar').addEventListener('click', startAR);
  q('add').addEventListener('click', addToCart);

  function renderUI() {
    q('plan').innerHTML = state.cells.map((c, i) =>
      `<button class="cfg-cell" type="button" data-cell="${i}" aria-pressed="${i === state.sel}" aria-label="${CELLS[i].label}: ${TYPES[c.t].label}, ${MATS[c.m].label}">${planIcon(c)}</button>`).join('');
    const cur = state.cells[state.sel];
    q('selpos').textContent = CELLS[state.sel].label;
    q('seldesc').textContent = cur.t === EMPTY ? 'Posição vazia' : `${TYPES[cur.t].label} · ${MATS[cur.m].label}${cur.r ? ` · rodado ${cur.r * 90}°` : ''}`;
    q('typelabel').textContent = TYPES[cur.t].label;
    q('matlabel').textContent = MATS[cur.m].label;
    root.querySelectorAll('[data-type]').forEach(b => b.setAttribute('aria-pressed', b.dataset.type === cur.t));
    root.querySelectorAll('[data-mat]').forEach(b => b.setAttribute('aria-pressed', b.dataset.mat === cur.m));
    const isEmpty = cur.t === EMPTY;
    q('rotate').disabled = isEmpty;
    root.querySelectorAll('[data-mat]').forEach(b => b.disabled = isEmpty);
    q('mats').classList.toggle('is-disabled', isEmpty);
    const n = filled().length;
    q('price').textContent = money(state.cells.reduce((s, c) => s + priceOf(c), 0));
    q('count').textContent = `${n} ${MOD_WORD}`;
    q('code').textContent = encode();
    // Only block when the variant is not found. Availability check removed —
     // products with inventoryPolicy: CONTINUE should always be addable to cart.
    const missing = filled().some(c => { const v = findVariant(c); return !CFG.demo && !v; });
    const btn = q('add');
    btn.disabled = missing || n === 0;
    q('ar').disabled = n === 0;
    q('msg').textContent = missing ? (LABELS.msgUnavailable || 'One of your chosen combinations is currently unavailable.') : (n === 0 ? (LABELS.msgEmpty || 'Please choose at least one module.') : '');
    try { history.replaceState(history.state, '', location.pathname + location.search + '#' + encode()); } catch (e) {}
  }

  let toastT;
  function toast(msg, ms = 2600) { const t = q('toast'); t.textContent = msg; t.hidden = false; clearTimeout(toastT); toastT = setTimeout(() => t.hidden = true, ms); }

  /* ---------- Carrinho ---------- */
  function cartItems() {
    const code = encode();
    let h = 0; for (const ch of code + Date.now()) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    const ref = 'CH-' + h.toString(36).slice(0, 6).toUpperCase();
    return state.cells.map((c, i) => {
      if (c.t === EMPTY) return null;
      const v = findVariant(c);
      const propSet = LABELS.propSet || 'Set';
      const propPos = LABELS.propPos || 'Position';
      return {
        id: v ? v.id : `VARIANTE_${TYPES[c.t].label}_${MATS[c.m].label}`,
        quantity: 1,
        properties: {
          [propSet]: ref,
          [propPos]: CELLS[i].label,
          '_orientation': `${c.r * 90}°`,
          '_config': code,
        },
      };
    }).filter(Boolean);
  }

  async function addToCart() {
    const btn = q('add'), label = btn.querySelector('.cfg-btn-text'), idle = label.textContent;
    const items = cartItems();
    if (CFG.demo) {
      q('json').textContent = JSON.stringify({ items }, null, 2);
      const d = q('dialog'); d.showModal ? d.showModal() : d.setAttribute('open', '');
      return;
    }
    btn.disabled = true; label.textContent = LABELS.msgAdding || 'Adding…';
    console.log('[cfg] add-to-cart items:', items);
    try {
      const res = await fetch(CFG.cartAddUrl || '/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      });
      console.log('[cfg] add.js status:', res.status);
      if (!res.ok) { const err = await res.json().catch(() => ({})); console.error('[cfg] add.js error:', err); throw new Error(err.description || err.message || `HTTP ${res.status}`); }
      const okData = await res.json().catch(() => null);
      console.log('[cfg] add.js response:', okData);
      document.dispatchEvent(new CustomEvent('cfg:cart:added', { detail: { items, code: encode() } }));
      const cart = window.theme && window.theme.Cart;
      if (cart && typeof cart.updateCart === 'function') {
        await cart.updateCart();
        label.textContent = LABELS.msgAdded || 'Added';
        if (hooks.close) hooks.close();
        if (typeof cart.showPopup === 'function') setTimeout(() => cart.showPopup(), hooks.close ? 380 : 0);
        setTimeout(() => { label.textContent = idle; btn.disabled = false; }, 2000);
      } else {
        location.href = CFG.cartUrl || '/cart';
      }
    } catch (e) {
      label.textContent = idle; btn.disabled = false;
      q('msg').textContent = `${LABELS.msgError || 'Could not add to cart'}: ${e.message}`;
    }
  }
  q('dialogclose')?.addEventListener('click', () => q('dialog').close());
  q('copy')?.addEventListener('click', async () => {
    try { await navigator.clipboard.writeText(q('json').textContent); toast('Copiado'); }
    catch (e) { const r = document.createRange(); r.selectNodeContents(q('json')); const s = getSelection(); s.removeAllRanges(); s.addRange(r); toast('Texto selecionado. Copie com Ctrl/Cmd+C'); }
  });

  /* ---------- 3D ---------- */
  const stage = q('stage'), canvas = q('canvas');
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1.12;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.xr.enabled = true;

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 0.75;

  const camera = new THREE.PerspectiveCamera(32, 1, 0.01, 50);
  const K = MOD.w / 0.4;
  const HOME = { pos: new THREE.Vector3(-0.95 * K, 1.05 * K, 1.75 * K), target: new THREE.Vector3(0, MOD.h * 0.4, 0) };
  camera.position.copy(HOME.pos);
  const controls = new OrbitControls(camera, canvas);
  controls.target.copy(HOME.target);
  controls.enableDamping = true; controls.dampingFactor = 0.08;
  controls.minDistance = 0.7 * K; controls.maxDistance = 4.5 * K;
  controls.maxPolarAngle = Math.PI * 0.47;
  controls.enablePan = false;
  controls.enableZoom = true;

  scene.add(new THREE.HemisphereLight(0xffffff, 0xd9d2c7, 0.55));
  const sun = new THREE.DirectionalLight(0xfff6ea, 1.6);
  sun.position.set(-1.2, 2.6, 1.6);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  Object.assign(sun.shadow.camera, { left: -1.1, right: 1.1, top: 1.1, bottom: -1.1, near: 0.5, far: 7 });
  sun.shadow.bias = -0.0004; sun.shadow.normalBias = 0.01;
  scene.add(sun);

  const table = new THREE.Group(); scene.add(table);
  const pieces = new THREE.Group(); table.add(pieces);
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(6, 6), new THREE.ShadowMaterial({ opacity: 0.2 }));
  ground.rotation.x = -Math.PI / 2; ground.receiveShadow = true; table.add(ground);
  const selLine = new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints([[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([x, z]) => new THREE.Vector3(x * (MOD.w / 2 + 0.015), 0.002, z * (MOD.w / 2 + 0.015)))),
    new THREE.LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.55 }));
  table.add(selLine);

  const texLoader = new THREE.TextureLoader();
  texLoader.setCrossOrigin('anonymous');
  const matCache = {};
  const material = k => {
    if (matCache[k]) return matCache[k];
    const tex = texLoader.load(MATS[k].texture);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = tex.wrapT = THREE.MirroredRepeatWrapping;
    tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
    return (matCache[k] = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.58, metalness: 0 }));
  };

  // Junta os painéis, normais planas e UV em projeção de caixa (veio vertical nas faces, frente-trás nos tampos)
  function prepGeometry(src) {
    src.updateMatrixWorld(true);
    const list = [];
    src.traverse(o => {
      if (!o.isMesh) return;
      let g = o.geometry.clone();
      g.applyMatrix4(o.matrixWorld);
      if (g.index) g = g.toNonIndexed();
      for (const k of Object.keys(g.attributes)) if (k !== 'position') g.deleteAttribute(k);
      list.push(g);
    });
    const g = mergeGeometries(list);
    g.computeBoundingBox();
    const bb = g.boundingBox, cx = (bb.min.x + bb.max.x) / 2, cz = (bb.min.z + bb.max.z) / 2;
    g.translate(-cx, -bb.min.y, -cz);            // centrar e assentar no chão
    const sz = bb.getSize(new THREE.Vector3());
    g.scale(MOD.w / sz.x, MOD.h / sz.y, MOD.w / sz.z); // ajustar às medidas reais do módulo
    g.computeVertexNormals();
    g.computeBoundingBox();
    const min = g.boundingBox.min, p = g.attributes.position, n = g.attributes.normal;
    const uv = new Float32Array(p.count * 2);
    for (let i = 0; i < p.count; i += 3) {
      const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i)), az = Math.abs(n.getZ(i));
      for (let j = i; j < i + 3; j++) {
        const x = p.getX(j) - min.x, y = p.getY(j) - min.y, z = p.getZ(j) - min.z;
        let u, v;
        if (ay >= ax && ay >= az) { u = x; v = z; } else if (ax >= az) { u = z; v = y; } else { u = x; v = y; }
        uv[j * 2] = u / TILE; uv[j * 2 + 1] = v / TILE;
      }
    }
    g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    return g;
  }

  const baseGeo = {};
  const pieceGeometry = (type, seed) => {
    const g = baseGeo[type].clone(), uv = g.attributes.uv;
    const ou = ((seed * 0.37) % 1) * 0.35, ov = ((seed * 0.61) % 1) * 0.35;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) + ou, uv.getY(i) + ov);
    return g;
  };

  const hitGeo = new THREE.PlaneGeometry(MOD.w, MOD.w).rotateX(-Math.PI / 2);
  const hitTargets = [];
  const slots = CELLS.map((c, i) => {
    const grp = new THREE.Group(); grp.position.set(c.x * HALF, 0, c.z * HALF); pieces.add(grp);
    const hit = new THREE.Mesh(hitGeo, new THREE.MeshBasicMaterial()); hit.visible = false; hit.userData.cell = i;
    hit.position.set(c.x * HALF, 0.001, c.z * HALF); table.add(hit); hitTargets.push(hit);
    return { grp, key: '' };
  });

  function buildScene() {
    if (!Object.keys(baseGeo).length) return;
    state.cells.forEach((c, i) => {
      const s = slots[i], key = c.t + c.m;
      if (s.key !== key) {
        s.grp.clear(); s.key = key;
        if (c.t === EMPTY) return;
        const mesh = new THREE.Mesh(pieceGeometry(c.t, i + 1), material(c.m));
        mesh.castShadow = mesh.receiveShadow = true;
        mesh.userData.cell = i;
        s.grp.add(mesh); s.key = key;
      }
      s.grp.rotation.y = -c.r * Math.PI / 2;
    });
    const sc = CELLS[state.sel];
    selLine.position.set(sc.x * HALF, 0, sc.z * HALF);
    const box = new THREE.Box3().setFromObject(pieces), cm = v => Math.round(v * 100);
    const dt = q('dimtext');
    if (box.isEmpty()) { q('dims').textContent = `— ${MOD_WORD}`; if (dt) dt.innerHTML = ''; return; }
    const size = box.getSize(new THREE.Vector3());
    q('dims').textContent = `${cm(size.x)} × ${cm(size.z)} × ${cm(size.y)} cm`;
    if (dt) dt.innerHTML = `Composição (C × L × A): ${cm(size.x)} × ${cm(size.z)} × ${cm(size.y)} cm<br>Cada módulo: ${cm(MOD.w)} × ${cm(MOD.w)} × ${cm(MOD.h)} cm`;
  }

  function update() { buildScene(); renderUI(); }

  const ray = new THREE.Raycaster(), ndc = new THREE.Vector2();
  let down = null;
  canvas.addEventListener('pointerdown', e => { down = [e.clientX, e.clientY]; });
  canvas.addEventListener('pointerup', e => {
    if (!down || Math.hypot(e.clientX - down[0], e.clientY - down[1]) > 6) return;
    const r = canvas.getBoundingClientRect();
    ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    ray.setFromCamera(ndc, camera);
    const hit = ray.intersectObjects([...pieces.children, ...hitTargets], true)[0];
    if (hit) { state.sel = hit.object.userData.cell; update(); }
  });

  let anim = null;
  function resetView() { anim = { t: 0, fp: camera.position.clone(), ft: controls.target.clone() }; }
  function stepAnim(dt) {
    if (!anim) return;
    anim.t = Math.min(1, anim.t + dt / 0.6);
    const k = 1 - Math.pow(1 - anim.t, 3);
    camera.position.lerpVectors(anim.fp, HOME.pos, k);
    controls.target.lerpVectors(anim.ft, HOME.target, k);
    if (anim.t >= 1) anim = null;
  }

  function resize() {
    const w = stage.clientWidth, h = stage.clientHeight;
    if (!w || !h || renderer.xr.isPresenting) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    // Mobile / narrow: widen FOV but keep object well-sized
    const isNarrow = w / h < 0.9 || w < 480;
    camera.fov = isNarrow ? 45 : 32;
    camera.zoom = isNarrow ? 1.0 : (w / h < 1.1 ? 1.18 : 1);
    camera.updateProjectionMatrix();
  }
  new ResizeObserver(resize).observe(stage);

  /* ---------- AR ---------- */
  let hitSource = null, reticle = null;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  async function startAR() {
    try {
      if (isIOS) {
        toast('A preparar o modelo para AR…');
        const { USDZExporter } = await import('three/addons/exporters/USDZExporter.js');
        selLine.visible = false; ground.visible = false;
        const data = await new USDZExporter().parseAsync(table);
        selLine.visible = true; ground.visible = true;
        const a = document.createElement('a');
        a.rel = 'ar';
        a.href = URL.createObjectURL(new Blob([data], { type: 'model/vnd.usdz+zip' }));
        a.appendChild(document.createElement('img'));
        a.click();
        return;
      }
      if (navigator.xr && await navigator.xr.isSessionSupported('immersive-ar')) return startWebXR();
      toast('Abra esta página no telemóvel (iPhone ou Android) para ver a mesa na sua sala.', 4200);
    } catch (e) {
      selLine.visible = true; ground.visible = true;
      toast('Não foi possível abrir a realidade aumentada neste dispositivo.', 4200);
    }
  }

  async function startWebXR() {
    const overlay = q('xr');
    const session = await navigator.xr.requestSession('immersive-ar', {
      requiredFeatures: ['hit-test'], optionalFeatures: ['dom-overlay'], domOverlay: { root: overlay },
    });
    overlay.hidden = false;
    renderer.xr.setReferenceSpaceType('local');
    await renderer.xr.setSession(session);
    hitSource = await session.requestHitTestSource({ space: await session.requestReferenceSpace('viewer') });
    if (!reticle) {
      reticle = new THREE.Mesh(new THREE.RingGeometry(0.1, 0.12, 40).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0xffffff }));
      reticle.matrixAutoUpdate = false; scene.add(reticle);
    }
    table.visible = false; selLine.visible = false;
    q('xrmsg').textContent = 'Aponte para o chão e toque para colocar';
    session.addEventListener('select', () => {
      if (!reticle.visible) return;
      table.position.setFromMatrixPosition(reticle.matrix);
      table.visible = true; q('xrmsg').textContent = 'Toque noutro sítio para mudar de lugar';
    });
    q('xrexit').onclick = () => session.end();
    session.addEventListener('end', () => {
      hitSource = null; reticle.visible = false; overlay.hidden = true;
      table.position.set(0, 0, 0); table.visible = true; selLine.visible = true; resize();
    });
  }

  /* ---------- Ciclo ---------- */
  const clock = new THREE.Clock();
  const loop = (t, frame) => {
    const dt = clock.getDelta();
    if (frame && hitSource) {
      const res = frame.getHitTestResults(hitSource);
      if (res.length) { const pose = res[0].getPose(renderer.xr.getReferenceSpace()); reticle.visible = true; reticle.matrix.fromArray(pose.transform.matrix); }
      else reticle.visible = false;
    } else { stepAnim(dt); controls.update(); }
    renderer.render(scene, camera);
  };

  /* ---------- Arranque ---------- */
  renderUI();
  const loader = new GLTFLoader();
  const b64buf = s => Uint8Array.from(atob(s), c => c.charCodeAt(0)).buffer;
  Promise.all(CFG.types.map(t => {
    const p = t.modelB64 ? loader.parseAsync(b64buf(t.modelB64), '') : loader.loadAsync(t.model);
    return p.then(g => { baseGeo[t.key] = prepGeometry(g.scene); });
  }))
    .then(() => { Object.keys(MATS).forEach(material); buildScene(); resize(); q('loading').hidden = true; })
    .catch(err => { q('loading').textContent = 'Não foi possível carregar os modelos 3D.'; console.error('[configurador-3d]', err); });

  // Só desenha enquanto a aba está aberta (poupa bateria)
  const api = { setActive(on) { renderer.setAnimationLoop(on ? loop : null); if (on) { clock.getDelta(); resize(); renderUI(); } } };
  if (!hooks.close) api.setActive(true);
  return api;
}
